import { useState, useEffect, useRef } from "react";
import CityPicker from "./CityPicker";
import { dataServerUrl } from "@/config/api";
import { fetchDistricts, fetchStreets, fetchRegionByGid, fetchPoiByGid } from '@/services/dataserver';
import type { AdminDivision } from '@/services/dataserver';
import CustomGridDrawing from './GridSearchView/CustomGridDrawing';

interface SearchFilterProps {
  city: string;
  onCityChange: (city: string) => void;
  onLocationSelect?: (lon: number, lat: number, name: string, data?: Record<string, any>) => void;
  onCheckTypeSelect?: (type: string) => void;
  onGridSelect?: (data: { type: string; data: any }) => void;
  mapSelectedStreet?: { gid: string; name: string } | null;
  drawingMode?: boolean;
  onDrawingChange?: (drawing: boolean) => void;
  aiKeyword?: string;
}

interface EnterpriseResult {
  name: string;
  addr: string;
  lon: number;
  lat: number;
  gid?: string | number;
  _raw?: Record<string, any>;
  [key: string]: any;
}

const checkTypes = [
  { label: '查小区', type: 'community', icon: 'ri-home-4-line', iconColor: 'text-emerald-600' },
  { label: '查楼宇', type: 'building', icon: 'ri-building-2-line', iconColor: 'text-orange-600' },
  { label: '查商铺', type: 'shop', icon: 'ri-store-2-line', iconColor: 'text-violet-600' },
  { label: '查园区', type: 'park', icon: 'ri-building-3-line', iconColor: 'text-amber-600' },
  { label: '查企业', type: 'enterprise', icon: 'ri-briefcase-4-line', iconColor: 'text-blue-600' },
  { label: '查商圈', type: 'businessDistrict', icon: 'ri-shopping-bag-3-line', iconColor: 'text-rose-600' },
];

export default function SearchFilter({ city, onCityChange, onLocationSelect, onCheckTypeSelect, onGridSelect, mapSelectedStreet, drawingMode, onDrawingChange, aiKeyword }: SearchFilterProps) {
  const [keyword, setKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<EnterpriseResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState<EnterpriseResult | null>(null);
  const [activeCheckType, setActiveCheckType] = useState<string | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [districtOptions, setDistrictOptions] = useState<AdminDivision[]>([]);
  const [streetOptions, setStreetOptions] = useState<AdminDivision[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<AdminDivision | null>(null);
  const [selectedStreet, setSelectedStreet] = useState<AdminDivision | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showStreetDropdown, setShowStreetDropdown] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const districtDropdownRef = useRef<HTMLDivElement>(null);
  const streetDropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async (pNum?: number) => {
    if (!keyword.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const safeCity = city.endsWith('市') ? city : city + '市';
      const currentPage = pNum ?? pageNum;
      const url = dataServerUrl('queryDataByToList', { name: keyword.trim(), cityName: safeCity, pageNum: currentPage, pageSize });
      const res = await fetch(url);
      const json = await res.json();
      // rows 平铺返回，每条记录通过 type 区分：cj(场景) / poi / qy(企业)
      const rows = json?.rows || [];
      const totalCount = json?.total || 0;
      const mapped = rows.map((item: any) => {
        const source = item.type === 'cj' ? 'cangjing' : item.type === 'qy' ? 'qy' : 'poi';
        return {
          ...item,
          name: item.name || '未知',
          addr: item.addr || '',
          lon: parseFloat(item.lon || 0),
          lat: parseFloat(item.lat || 0),
          gid: item.objectid || item.gid || '',
          poi_scene: item['提取'] || '',
          source,
          _raw: item,
        };
      });
      setSearchResults(mapped);
      setTotal(totalCount || mapped.length);
    } catch (error) {
      console.error('搜索失败:', error);
      setSearchResults([]);
    }
  };

  // 当关键词变化时进行搜索（防抖）
  useEffect(() => {
    // 选中搜索结果导致的关键词变化，不再重复搜索
    if (selectingResultRef.current) {
      selectingResultRef.current = false;
      return;
    }

    if (!keyword.trim()) {
      setSearchResults([]);
      setSelectedResult(null);
      setTotal(0);
      return;
    }

    // 关键词变化时重置到第一页
    setPageNum(1);

    const timer = setTimeout(() => {
      handleSearch(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [keyword, city]);

  // 翻页时重新搜索（不防抖）
  useEffect(() => {
    if (keyword.trim() && pageNum > 1) {
      handleSearch(pageNum);
    }
  }, [pageNum]);

  // 处理选择搜索结果
  const handleSelectResult = async (result: EnterpriseResult) => {
    selectingResultRef.current = true;
    setKeyword(result.name);
    setSelectedResult(result);
    setShowResults(false);

    const raw = result._raw || {};

    // 场景 → 获取场景面详情 + 加载场景内企业列表
    if (result.source === 'cangjing' && onGridSelect) {
      const pid = raw.pid ?? raw.gid ?? raw.objectid ?? '';
      const gid = raw.gid ?? raw.objectid ?? '';
      let sceneGeom = raw.geom || '';
      // 先从场景详情接口获取 geometry
      try {
        const sceneItems = await fetchRegionByGid(gid);
        const sceneItem = Array.isArray(sceneItems) ? sceneItems[0] : sceneItems;
        if (sceneItem?.geom) {
          sceneGeom = sceneItem.geom;
        }
      } catch (err) {
        console.warn('获取场景面详情失败:', err);
      }
      // 再获取场景内企业列表
      let enterprises: any[] = [];
      try {
        const detailUrl = dataServerUrl('selectQYByCangJingId', { pid });
        const detailRes = await fetch(detailUrl);
        const detailJson = await detailRes.json();
        enterprises = detailJson?.rows || detailJson?.data || [];
      } catch (err) {
        console.error('获取场景企业列表失败:', err);
      }
      onGridSelect({
        type: 'scene_single',
        data: {
          name: result.name,
          geom: sceneGeom,
          '提取': raw['提取'] || '',
          extractType: raw['提取'] || '',
          objectid: raw.objectid || '',
          gid: raw.gid || '',
          enterprises,
          fromAgent: isFromAgentRef.current || undefined,
        },
      });
      return;
    }

    // 企业 → 加载企业详情并定位
    if (result.source === 'qy') {
      const id = raw.pid ?? raw.gid ?? raw.objectid ?? '';
      try {
        const detailUrl = dataServerUrl('selectQYById', { id });
        const detailRes = await fetch(detailUrl);
        const detailJson = await detailRes.json();
        const enterpriseData = detailJson?.data || detailJson?.rows || detailJson;
        const entLat = parseFloat(enterpriseData?.lat || 0);
        const entLon = parseFloat(enterpriseData?.lon || 0);
        if (entLat && entLon) {
          onLocationSelect?.(entLon, entLat, result.name, { type: 'qy_detail', data: enterpriseData, _raw: raw });
        } else {
          onLocationSelect?.(0, 0, result.name, { type: 'qy_detail', data: enterpriseData, _raw: raw });
        }
      } catch (err) {
        console.error('获取企业详情失败:', err);
      }
      return;
    }

    // POI → 获取 POI 详情并定位
    if (result.source === 'poi') {
      const gid = raw.gid ?? raw.objectid ?? '';
      try {
        const poiList = await fetchPoiByGid(gid);
        const poiData = Array.isArray(poiList) ? poiList[0] : poiList;
        if (poiData) {
          const poiLat = parseFloat(poiData.lat || 0);
          const poiLon = parseFloat(poiData.lon || 0);
          if (poiLat && poiLon) {
            onLocationSelect?.(poiLon, poiLat, result.name, { type: 'poi', data: poiData, _raw: raw });
          }
        }
      } catch (err) {
        console.error('获取 POI 详情失败:', err);
      }
      return;
    }
  };

  // 城市切换时加载区县列表
  useEffect(() => {
    const safeCity = city.endsWith('市') ? city : city + '市';
    setRegionLoading(true);
    setSelectedDistrict(null);
    setSelectedStreet(null);
    setStreetOptions([]);
    fetchDistricts(safeCity, 2, 140)
      .then(list => setDistrictOptions(list || []))
      .catch(err => console.error('获取区县失败:', err))
      .finally(() => setRegionLoading(false));
  }, [city]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setKeyword(value);
    setSelectedResult(null);
    if (!value.trim()) {
      setSearchResults([]);
      setShowResults(false);
      // 清除地图上的场景面和标记点，重置视角
      if (onLocationSelect) {
        onLocationSelect(0, 0, '', { clearAll: true });
      }
    } else {
      setShowResults(true);
    }
  };

  // 处理输入框焦点 - 显示下拉框
  const handleInputFocus = () => {
    if (keyword.trim()) {
      setShowResults(true);
    }
  };

  // 点击搜索按钮 - 传递选中的位置数据给地图
  const handleManualSearch = () => {
    if (selectedResult && onLocationSelect) {
      onLocationSelect(selectedResult.lon, selectedResult.lat, selectedResult.name, selectedResult._raw || selectedResult);
    } else if (searchResults.length > 0 && onLocationSelect) {
      const firstResult = searchResults[0];
      onLocationSelect(firstResult.lon, firstResult.lat, firstResult.name, firstResult._raw || firstResult);
    }
  };

  const handleCheckTypeClick = (type: string) => {
    setActiveCheckType(type === activeCheckType ? null : type);
    onCheckTypeSelect?.(type);
  };

  // --- 区县/街道选择 ---
  const handleDistrictSelect = (district: AdminDivision) => {
    setSelectedDistrict(district);
    setSelectedStreet(null);
    setStreetOptions([]);

    if (onGridSelect) {
      onGridSelect({ type: 'district', data: district });
    }

    fetchStreets(district.name, 3, district.gid)
      .then(streets => {
        setStreetOptions(streets || []);
        if (onGridSelect && streets?.length) {
          onGridSelect({ type: 'district_streets', data: streets });
        }
      })
      .catch(err => console.error('获取街道失败:', err));
  };

  const handleDistrictClear = () => {
    setSelectedDistrict(null);
    setSelectedStreet(null);
    setStreetOptions([]);
    onGridSelect?.({ type: 'district', data: null });
  };

  const handleStreetSelect = (street: AdminDivision) => {
    setSelectedStreet(street);
    if (onGridSelect) {
      onGridSelect({ type: 'street', data: street });
    }
  };

  const handleStreetClear = () => {
    setSelectedStreet(null);
    onGridSelect?.({ type: 'street', data: null });
  };

  // 点击外部关闭区县/街道下拉框
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (districtDropdownRef.current && !districtDropdownRef.current.contains(e.target as Node)) {
        setShowDistrictDropdown(false);
      }
      if (streetDropdownRef.current && !streetDropdownRef.current.contains(e.target as Node)) {
        setShowStreetDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 地图点击街道时，同步面板中的街道选择
  useEffect(() => {
    if (mapSelectedStreet) {
      // 优先从已加载的 streetOptions 中匹配
      const match = streetOptions.find(
        s => String(s.gid) === String(mapSelectedStreet.gid) || s.name === mapSelectedStreet.name
      );
      if (match) {
        setSelectedStreet(match);
      } else if (!selectedDistrict) {
        // 区县未选择时，用传入的 name 构造一个虚拟选择，面板上至少能显示名称
        setSelectedStreet({
          name: mapSelectedStreet.name,
          gid: Number(mapSelectedStreet.gid),
        } as AdminDivision);
      }
    } else {
      // 地图取消街道选择时同步清除
      setSelectedStreet(null);
    }
  }, [mapSelectedStreet]);

  // ===== AI 关键词自动搜索 =====
  const lastAiKeywordRef = useRef('');
  const aiAutoSelectRef = useRef(false);
  /** 标记当前自动选择是否来自智能体，用于地图组件区分行为 */
  const isFromAgentRef = useRef(false);
  /** 标记当前选择是否来自选中结果（阻止 keyword 变化再次触发搜索） */
  const selectingResultRef = useRef(false);

  // 收到 AI 关键词 → 填入搜索框、触发搜索
  useEffect(() => {
    if (aiKeyword && aiKeyword !== lastAiKeywordRef.current) {
      lastAiKeywordRef.current = aiKeyword;
      setKeyword(aiKeyword);
      setSelectedResult(null);
      setShowResults(true);
      aiAutoSelectRef.current = true;
    }
  }, [aiKeyword]);

  // AI 搜索结果返回后，自动选中第一个（用 ref 避免触发额外渲染）
  useEffect(() => {
    if (aiAutoSelectRef.current && searchResults.length > 0) {
      aiAutoSelectRef.current = false;
      isFromAgentRef.current = true;
      const timer = setTimeout(() => {
        handleSelectResult(searchResults[0]);
        isFromAgentRef.current = false;
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [searchResults]);

  return (
    <div className="absolute top-20 left-4 z-[1000] w-80">
      <div className="bg-white/92 backdrop-blur-md border border-slate-200/80 rounded-xl shadow-lg">

        {/* ===== 搜索区 ===== */}
        <div className="p-3 pb-2 space-y-2">
          {/* 搜索框（含下拉结果） */}
          <div className="relative" ref={searchContainerRef}>
            <div className="flex items-center bg-white/80 border border-slate-200 rounded-lg transition-all duration-200 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100">
              <div className="border-r border-slate-200/60 pr-1">
                <CityPicker city={city} onSelect={onCityChange} onLocationSelect={onLocationSelect} onGridSelect={onGridSelect} inline containerRef={searchContainerRef} mapSelectedStreet={mapSelectedStreet} />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={keyword}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder="搜索地址、企业、小区..."
                className="bg-transparent text-slate-700 placeholder-slate-400 text-sm pl-2 pr-2 py-2 outline-none flex-1 min-w-0"
              />
              {keyword && (
                <button
                  onClick={() => { setKeyword(""); setShowResults(false); onLocationSelect?.(0, 0, '', { clearAll: true }); inputRef.current?.focus(); }}
                  className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-200/70 hover:bg-slate-300/80 text-slate-500 hover:text-slate-700 transition-colors cursor-pointer mr-1 flex-shrink-0"
                >
                  <i className="ri-close-line text-[10px]" />
                </button>
              )}
              <button
                onClick={handleManualSearch}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-r-lg flex-shrink-0 -mr-px"
              >
                搜索
              </button>
            </div>

            {/* 搜索结果下拉 - 直接出现在搜索框下方 */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-lg shadow-lg z-[2000]">
                <div className="max-h-60 overflow-y-auto">
                  {searchResults.map((result, idx) => (
                    <div
                      key={result.gid || idx}
                      onClick={() => handleSelectResult(result)}
                      className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm border-b border-slate-100 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded flex items-center justify-center text-xs flex-shrink-0 ${
                          result.source === 'cangjing'
                            ? 'bg-amber-100 text-amber-600'
                            : result.source === 'qy'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-rose-100 text-rose-600'
                        }`}>
                          <i className={`${
                            result.source === 'cangjing'
                              ? 'ri-shape-2-line'
                              : result.source === 'qy'
                              ? 'ri-briefcase-4-line'
                              : 'ri-map-pin-2-line'
                          } text-[11px]`}></i>
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 truncate">{result.name}</div>
                          {result.addr && (
                            <div className="text-slate-500 text-xs mt-0.5 truncate">{result.addr}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* 分页控件 */}
                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/80 rounded-b-lg">
                  <span className="text-xs text-slate-400">
                    共 {total} 条
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPageNum(p => Math.max(1, p - 1))}
                      disabled={pageNum <= 1}
                      className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      上一页
                    </button>
                    <span className="text-xs text-slate-500 px-2">
                      {pageNum}/{Math.max(1, Math.ceil(total / pageSize))}
                    </span>
                    <button
                      onClick={() => setPageNum(p => p + 1)}
                      disabled={pageNum >= Math.ceil(total / pageSize)}
                      className="px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      下一页
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showResults && searchResults.length === 0 && keyword.trim() && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-lg shadow-lg p-3 text-slate-500 text-sm z-[2000]">
                未找到相关结果
              </div>
            )}
          </div>

          {/* 快捷查询 pills */}
          <div className="flex items-center flex-wrap gap-1">
            {checkTypes.map((item) => (
              <button
                key={item.type}
                onClick={() => handleCheckTypeClick(item.type)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCheckType === item.type
                    ? 'text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700'
                }`}
                style={activeCheckType === item.type ? {
                  backgroundColor: item.iconColor === 'text-emerald-600' ? '#059669' :
                    item.iconColor === 'text-orange-600' ? '#ea580c' :
                    item.iconColor === 'text-violet-600' ? '#7c3aed' :
                    item.iconColor === 'text-amber-600' ? '#d97706' :
                    item.iconColor === 'text-blue-600' ? '#2563eb' : '#e11d48'
                } : {}}
              >
                <i className={`${item.icon} text-xs ${activeCheckType === item.type ? 'text-white' : item.iconColor}`}></i>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== 区域筛选 ===== */}
        <div className="px-3 pt-1 pb-2">
          <div className="flex items-center gap-3 mb-2.5">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">区域筛选</span>
            <span className="flex-1 h-px bg-slate-100" />
          </div>

          {/* 区县 | 街道 一排 */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1" ref={districtDropdownRef}>
              <button
                onClick={() => !regionLoading && setShowDistrictDropdown(!showDistrictDropdown)}
                className={`w-full flex items-center gap-1 border rounded px-2 py-1.5 text-xs transition-all cursor-pointer shadow-sm ${
                  regionLoading
                    ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                } ${selectedDistrict ? 'border-blue-300 bg-blue-50/30 shadow-sm' : ''}`}
              >
                <i className={`ri-government-line text-xs flex-shrink-0 ${selectedDistrict ? 'text-blue-500' : 'text-slate-400'}`}></i>
                <span className={`flex-1 text-left truncate ${!selectedDistrict ? "text-slate-400" : "text-slate-700"}`}>
                  {regionLoading ? '加载中...' : (selectedDistrict?.name || '区县')}
                </span>
                {!regionLoading && (
                  <i className={`ri-arrow-down-s-line text-slate-400 transition-transform text-xs flex-shrink-0 ${showDistrictDropdown ? "rotate-180" : ""}`} />
                )}
              </button>
              {showDistrictDropdown && (
                <div className="absolute top-full left-0 mt-1 z-50 w-full bg-white border border-slate-200 rounded shadow-lg overflow-hidden max-h-44 overflow-y-auto">
                  <button
                    onClick={() => { setShowDistrictDropdown(false); handleDistrictClear(); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50 whitespace-nowrap transition-colors cursor-pointer border-b border-slate-100"
                  >
                    清除选择
                  </button>
                  {districtOptions.map(d => (
                    <button
                      key={d.gid}
                      onClick={() => { setShowDistrictDropdown(false); handleDistrictSelect(d); }}
                      className={`w-full text-left px-3 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        selectedDistrict?.gid === d.gid
                          ? "text-slate-900 bg-slate-100 font-medium"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative flex-1" ref={streetDropdownRef}>
              <button
                onClick={() => selectedDistrict && setShowStreetDropdown(!showStreetDropdown)}
                disabled={!selectedDistrict}
                className={`w-full flex items-center gap-1 border rounded px-2 py-1.5 text-xs transition-all cursor-pointer shadow-sm ${
                  !selectedDistrict
                    ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                } ${selectedStreet ? 'border-purple-300 bg-purple-50/30 shadow-sm' : ''}`}
              >
                <i className={`ri-road-map-line text-xs flex-shrink-0 ${selectedStreet ? 'text-purple-500' : 'text-slate-400'}`}></i>
                <span className={`flex-1 text-left truncate ${!selectedStreet ? "text-slate-400" : "text-slate-700"}`}>
                  {selectedStreet?.name || '街道'}
                </span>
                {selectedDistrict && (
                  <i className={`ri-arrow-down-s-line text-slate-400 transition-transform text-xs flex-shrink-0 ${showStreetDropdown ? "rotate-180" : ""}`} />
                )}
              </button>
              {showStreetDropdown && selectedDistrict && (
                <div className="absolute top-full left-0 mt-1 z-50 w-full bg-white border border-slate-200 rounded shadow-lg overflow-hidden max-h-44 overflow-y-auto">
                  <button
                    onClick={() => { setShowStreetDropdown(false); handleStreetClear(); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50 whitespace-nowrap transition-colors cursor-pointer border-b border-slate-100"
                  >
                    清除选择
                  </button>
                  {streetOptions.map(s => (
                    <button
                      key={s.gid}
                      onClick={() => { setShowStreetDropdown(false); handleStreetSelect(s); }}
                      className={`w-full text-left px-3 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        selectedStreet?.gid === s.gid
                          ? "text-slate-900 bg-slate-100 font-medium"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 已选区域标签 */}
          {selectedDistrict && (
            <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                <i className="ri-government-line text-[10px]"></i>
                {selectedDistrict.name}
                <button onClick={handleDistrictClear} className="ml-0.5 text-blue-400 hover:text-blue-700 cursor-pointer transition-colors">
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </span>
              {selectedStreet && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">
                  <i className="ri-road-map-line text-[10px]"></i>
                  {selectedStreet.name}
                  <button onClick={handleStreetClear} className="ml-0.5 text-purple-400 hover:text-purple-700 cursor-pointer transition-colors">
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              )}
              <button
                onClick={handleDistrictClear}
                className="ml-auto text-[10px] text-slate-400 hover:text-red-500 cursor-pointer transition-colors flex items-center gap-0.5"
              >
                <i className="ri-delete-bin-line text-[10px]"></i>
                清空
              </button>
            </div>
          )}
        </div>

        {/* ===== 地图工具 ===== */}
        {onDrawingChange && (
          <div className="px-3 pb-3 pt-1">
            <div className="flex items-center gap-3 mb-2.5">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">地图工具</span>
              <span className="flex-1 h-px bg-slate-100" />
            </div>
            <CustomGridDrawing drawingMode={!!drawingMode} onDrawingChange={onDrawingChange} />
          </div>
        )}

      </div>
    </div>
  );
}